import { FC, useCallback, useEffect, useRef } from 'react';

export type AudioSampleRecorderProps = {
  recording?: boolean;
  onSampleRecorded: (sample: Blob) => void;
};

export const AudioSampleRecorder: FC<AudioSampleRecorderProps> = ({ recording = false, onSampleRecorded }) => {
  const streamRef = useRef<MediaStream | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const onCleanUp = useCallback(() => {
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current.ondataavailable = null;
      mediaRecorderRef.current.onstop = null;
      mediaRecorderRef.current = null;
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
  }, []);
  const onSampleRecordedRef = useRef(onSampleRecorded);
  onSampleRecordedRef.current = onSampleRecorded;
  const onStartRecording = useCallback(async () => {
    const audioChunks: Blob[] = [];

    streamRef.current = await navigator.mediaDevices.getUserMedia({ audio: true });
    mediaRecorderRef.current = new MediaRecorder(streamRef.current);

    mediaRecorderRef.current.ondataavailable = (event) => {
      audioChunks.push(event.data);
    };
    mediaRecorderRef.current.onstop = () => {
      const audioBlob = new Blob(audioChunks, { type: 'audio/wav' });

      onSampleRecordedRef.current(audioBlob);
      onCleanUp();
    };

    mediaRecorderRef.current.start();
  }, [onCleanUp]);
  const onStopRecording = useCallback(() => {
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
    }
  }, []);

  useEffect(() => {
    if (recording) {
      onStartRecording();
    } else {
      onStopRecording();
    }
  }, [recording, onStartRecording, onCleanUp]);

  useEffect(() => {
    return onCleanUp;
  }, [onCleanUp]);

  return null;
};
